import { useTranslation } from '@lt/localization';
import cn from 'classnames';
import React from 'react';
import { connect } from 'react-redux';
import { Route, withRouter } from 'react-router-dom';

// Components:
import {
  MobileHotelSummary,
  MobileTourSummary,
} from 'checkout/components/Summary';
import Payment3dsPopup from 'src/components/Payment/Payment3dsPopup';
import { getIsHotelOnly } from 'src/store/view/selectors';
import { sendPageviewAction } from 'src/store/analytics/actions';
import FullScreenPreloader from '../../../../../../components/Auth/FullScreenPreloader';
import Modal from '../../../../../../components/Modal';
import { PaymentHidden3dsPopup } from '../../../../../../components/Payment/PaymentHidden3dsPopup';
import AboutRoomPopup from '../../../../components/AboutRoom/AboutRoomPopup';
import ErrorPopup from '../../../../components/ErrorPopup';
import MobileHeader from '../../../../components/MobileHeader/MobileHeader';
import OrderSuccess from '../../../../components/OrderSuccess';
import Sidebar from '../../../../components/Sidebar';
import SimilarPackagesContainer from '../../../../components/SimilarPackages/SimilarPackagesContainer';
import TransitionSwitch from '../../../../components/TransitionSwitch';
import { EmptyTimeTooltip } from '../../components/Flights';
import FlightsLuggageTooltip from '../../components/Flights/Luggage/FlightsLuggageTooltip';
import Hotel from '../../components/Hotel';
import withMobile from '../../hocs/withMobile';
import { FlightTransferHint } from '../Flights';
import Header from '../Header';
import ReactHint from '../Hint/HintFactory';
// Constants:
import {
  BASE_CLASSNAME,
  ROUTE_TRANSITION_TIME,
  TOURISTS_PATH,
} from '../../../../store/constants';
import {
  CHECKOUT_CONTAINER,
  CHECKOUT_CONTAINER_MOBILE,
  MOBILE_CHECKOUT,
} from '../../../../store/constants/classNames';
import { getCheckoutStep, routesList } from './utils';

// Selectors:
import {
  getPaymentIsProcessing,
  getShow3dsPopup,
} from '../../../../../../store/payment/selectors';
import {
  getOrderIsCreating,
  getOrderIsFinished,
  getOrderIsProcessing,
  getSelectedFlightData,
} from '../../../../store/selectors/order';
import { getPath } from '../../../../store/selectors/router';

import {
  getArchivedAt,
  getIsFuture,
  getIsManualPackage,
} from '../../../../store/selectors/package';
import { getSimilarPackagesHasAdjustParams } from '../../../../store/selectors/similar-packages';

import {
  routeTransitionEndAction,
  routeTransitionStartAction,
} from '../../../../store/actions/view';

// Utils:
import MobileHeaderTitle from '../../../../components/MobileHeader/MobileHeaderTitle';
import { getFullPath, getStepPath } from '../../../../utils/globalUtils';
import ArchivePackageBanner from '../../components/Package/ArchivePackageBanner';

const Layout = (props) => {
  const {
    path,
    isCreating,
    isProcessing,
    isPaymentProcessing,
    show3dsPopup,
    isFinished,
    routeTransitionStart,
    routeTransitionEnd,
    isMobile,
    hasAdjustParams,
    archivedAt,
    isFuture,
    isHotelOnly,
    sendPageview,
  } = props;

  const routesWithActualPath = routesList.map((item) => ({
    ...item,
    path: getFullPath(item.path),
  }));
  const { t } = useTranslation();

  const onTransitionEnd = () => {
    routeTransitionEnd();
    /**
     * Подменяется название компонента на flights для аналитики,
     * если пользователь переходит на первый шаг чекаута
     */
    const step = getCheckoutStep(path);
    if (!step) return;
    sendPageview(`package_checkout_${step}`);
  };

  const preloaderText = isCreating
    ? t('checkout.preloader.booking')
    : t('checkout.preloader.payment');
  const isOrderProcessing = isCreating || isProcessing || isPaymentProcessing;

  const isArchived = archivedAt && !isFuture;

  return (
    <div className={cn({ [MOBILE_CHECKOUT]: isMobile })}>
      <ReactHint autoPosition events attribute="data-text-hint" />

      {/* хинт может отображаться и в сайдбаре и в перелётах */}
      <ReactHint
        autoPosition
        attribute="data-luggage"
        className="react-hint"
        events
        onRenderContent={(target, content) =>
          content && (
            <div className={!isMobile && 'react-hint__content'}>
              <FlightsLuggageTooltip luggage={JSON.parse(content)} hasTitle />
            </div>
          )
        }
      />
      {!isMobile && (
        <ReactHint
          events
          attribute="data-route-info"
          className="react-hint react-hint--top react-hint--offset-top"
          onRenderContent={(target, content) =>
            content && (
              <div className="react-hint__content">
                <FlightTransferHint {...JSON.parse(content)} />
              </div>
            )
          }
        />
      )}
      <ReactHint
        events
        attribute="data-empty-time"
        className="react-hint"
        onRenderContent={(target, content) => (
          <EmptyTimeTooltip date={content} />
        )}
      />
      <ErrorPopup />
      <Payment3dsPopup />
      <PaymentHidden3dsPopup />
      <AboutRoomPopup />
      {isHotelOnly ? <MobileHotelSummary /> : <MobileTourSummary />}

      <div
        className={cn(CHECKOUT_CONTAINER, {
          [CHECKOUT_CONTAINER_MOBILE]: isMobile,
        })}
      >
        {!isFinished && (
          <>
            {isArchived && <ArchivePackageBanner />}
            {!isArchived && (isMobile ? <MobileHeader /> : <Header />)}
            {isMobile && getStepPath(path) !== TOURISTS_PATH && (
              <MobileHeaderTitle />
            )}
            <Route path="/:id" exact component={Hotel} />
          </>
        )}

        {!isArchived && !hasAdjustParams && (
          <div className={`${BASE_CLASSNAME}__main`}>
            {!isMobile && (
              <aside>
                <Sidebar />
              </aside>
            )}
            <section>
              {isFinished ? (
                <OrderSuccess />
              ) : (
                <TransitionSwitch
                  currentPath={path}
                  routes={routesWithActualPath}
                  transitionTime={ROUTE_TRANSITION_TIME}
                  onTransitionStart={routeTransitionStart}
                  onTransitionEnd={onTransitionEnd}
                />
              )}
            </section>
          </div>
        )}
      </div>

      {!isArchived && <SimilarPackagesContainer />}

      {isOrderProcessing && !show3dsPopup && (
        <Modal>
          <FullScreenPreloader text={preloaderText} />
        </Modal>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  path: getPath(state),
  isCreating: getOrderIsCreating(state),
  isProcessing: getOrderIsProcessing(state),
  isPaymentProcessing: getPaymentIsProcessing(state),
  isFinished: getOrderIsFinished(state),
  show3dsPopup: getShow3dsPopup(state),
  isManualPackage: getIsManualPackage(state),
  hasAdjustParams: getSimilarPackagesHasAdjustParams(state),
  selectedFlightsData: getSelectedFlightData(state),
  archivedAt: getArchivedAt(state),
  isFuture: getIsFuture(state),
  isHotelOnly: getIsHotelOnly(state),
});

const mapDispatchToProps = {
  routeTransitionStart: routeTransitionStartAction,
  routeTransitionEnd: routeTransitionEndAction,
  sendPageview: sendPageviewAction,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withRouter(withMobile(Layout)));
